import React, { useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../../api/apiService';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserContext } from '../../contexts/UserContext';
import type { PublishVenue } from '../../types/publish-venue';
import {
  PUBLISH_VENUE_PLATFORM_OPTIONS,
  isValidPublishVenueDomainUrl,
  platformLabelForValue,
} from '../../constants/publishVenues';
import { PasswordVisibilityToggle } from '../account/PasswordInputs';
import { normalizePublishVenuesListResponse } from '../../utils/publishVenuesResponse';

/** Shared sizing so Name / Domain / Platform line up in a row (matches MUI small outlined ~40px input). */
const formRowFieldBaseSx = {
  margin: 0,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    minHeight: 40,
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-input': {
    py: 1,
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' },
  '& .MuiInputLabel-root': { color: '#a1a1aa', fontSize: '0.875rem' },
  '& .MuiFormHelperText-root': {
    minHeight: 40,
    mx: 0,
    mt: '6px',
  },
} as const;

/** Top-align columns so inputs line up; helper area keeps similar height (long copy uses ellipsis). */
const formRowControlColumnSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
} as const;

/** Outlined small TextField label + input ~this offset; aligns switch / icon with input row. */
const formRowInlineControlMt = { xs: 0, sm: '22px' } as const;

type PlatformOption = (typeof PUBLISH_VENUE_PLATFORM_OPTIONS)[number];

export const PublishVenuesList = () => {
  const { user } = useContext(UserContext);
  const ap = user?.permissions?.analytic_profiles;
  const canRead = Boolean(ap?.read);
  const canCreate = Boolean(ap?.create);
  const canUpdate = Boolean(ap?.update);
  const canDelete = Boolean(ap?.delete);

  const [venues, setVenues] = useState<PublishVenue[]>([]);
  const [isLoading, setIsLoading] = useState(canRead);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newPlatform, setNewPlatform] = useState<PlatformOption | null>(null);
  const [newEnabled, setNewEnabled] = useState(true);
  const [newCMSKey, setNewCMSKey] = useState('');
  const [showNewCMSKey, setShowNewCMSKey] = useState(false);
  const [showEditCMSKey, setShowEditCMSKey] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [editPlatform, setEditPlatform] = useState<PlatformOption | null>(null);
  const [editEnabled, setEditEnabled] = useState(true);
  const [editCMSKey, setEditCMSKey] = useState('');
  const [editClearCMSKey, setEditClearCMSKey] = useState(false);

  const fetchVenues = useCallback(async () => {
    if (!canRead) return;
    try {
      setIsLoading(true);
      const res = await api.get('/api/publish-venues');
      setVenues(normalizePublishVenuesListResponse(res));
    } catch {
      setVenues([]);
    } finally {
      setIsLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    if (canRead) {
      void fetchVenues();
    } else {
      setIsLoading(false);
    }
  }, [canRead, fetchVenues]);

  const run = async (fn: () => Promise<void>) => {
    setSaving(true);
    setFormError(null);
    try {
      await fn();
    } catch {
      setFormError('Request failed. Check the form and try again.');
    } finally {
      setSaving(false);
    }
  };

  const addDisabled =
    saving ||
    !newName.trim() ||
    !isValidPublishVenueDomainUrl(newDomain) ||
    newPlatform == null ||
    (newEnabled && !newCMSKey.trim());

  const handleAdd = () =>
    run(async () => {
      if (addDisabled || !newPlatform) return;
      if (newEnabled && !newCMSKey.trim()) {
        setFormError('Set a CMS authentication token before enabling this venue.');
        return;
      }
      const payload: Record<string, unknown> = {
        name: newName.trim(),
        domain: newDomain.trim(),
        platform: newPlatform.value,
        enabled: newEnabled,
      };
      if (newCMSKey.trim()) {
        payload.cms_key = newCMSKey.trim();
      }
      const created = (await api.post('/api/publish-venues', payload)) as PublishVenue;
      setNewName('');
      setNewDomain('');
      setNewPlatform(null);
      setNewEnabled(true);
      setNewCMSKey('');
      setShowNewCMSKey(false);
      if (canRead) {
        await fetchVenues();
      } else {
        setVenues((prev) => [...prev, created]);
      }
    });

  const startEdit = (v: PublishVenue) => {
    setEditingId(v.id);
    setEditName(v.name);
    setEditDomain(v.domain ?? '');
    setEditPlatform(PUBLISH_VENUE_PLATFORM_OPTIONS.find((o) => o.value === (v.platform ?? '')) ?? null);
    setEditEnabled(Boolean(v.enabled));
    setEditCMSKey('');
    setEditClearCMSKey(false);
    setShowEditCMSKey(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditCMSKey('');
    setEditClearCMSKey(false);
    setShowEditCMSKey(false);
  };

  const editSaveDisabled =
    saving ||
    !editName.trim() ||
    !isValidPublishVenueDomainUrl(editDomain) ||
    editPlatform == null;

  const handleSaveEdit = () =>
    run(async () => {
      if (!editingId || editSaveDisabled || !editPlatform) return;
      const editingVenue = venues.find((x) => x.id === editingId);
      const willHaveCMSKey =
        Boolean(editCMSKey.trim()) || (Boolean(editingVenue?.cms_key_configured) && !editClearCMSKey);
      if (editEnabled && !willHaveCMSKey) {
        setFormError('Set a CMS authentication token before enabling this venue.');
        return;
      }
      const body: Record<string, unknown> = {
        name: editName.trim(),
        domain: editDomain.trim(),
        platform: editPlatform.value,
        enabled: editEnabled,
      };
      if (editClearCMSKey) {
        body.cms_key = '';
      } else if (editCMSKey.trim()) {
        body.cms_key = editCMSKey.trim();
      }
      const updated = (await api.put(`/api/publish-venues/${editingId}`, body)) as PublishVenue;
      setEditingId(null);
      if (canRead) {
        await fetchVenues();
      } else {
        setVenues((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      }
    });

  const handleToggleEnabled = (v: PublishVenue, enabled: boolean) => {
    if (enabled && !v.cms_key_configured) {
      setFormError('Set a CMS authentication token before enabling this venue.');
      return;
    }
    void run(async () => {
      const updated = (await api.put(`/api/publish-venues/${v.id}`, { enabled })) as PublishVenue;
      if (canRead) {
        await fetchVenues();
      } else {
        setVenues((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      }
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this publish venue?')) return;
    try {
      await api.delete(`/api/publish-venues/${id}`);
      setVenues((prev) => prev.filter((v) => v.id !== id));
      setDeleteError(null);
    } catch {
      setDeleteError('Failed to delete publish venue');
    }
  };

  const platformAutocomplete = (
    value: PlatformOption | null,
    onChange: (v: PlatformOption | null) => void,
    label: string,
    disabled?: boolean
  ) => (
    <Autocomplete
      disabled={disabled || saving}
      options={[...PUBLISH_VENUE_PLATFORM_OPTIONS]}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          minHeight: 40,
          color: '#fafafa',
          alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' },
        '& .MuiInputLabel-root': { color: '#a1a1aa', fontSize: '0.875rem' },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search platform…"
          size="small"
          margin="none"
          helperText=" "
          FormHelperTextProps={{ sx: { minHeight: 40, m: 0, mt: '6px' } }}
          sx={{ ...formRowFieldBaseSx }}
        />
      )}
      componentsProps={{
        paper: {
          sx: {
            bgcolor: '#111',
            border: '1px solid #27272a',
            '& .MuiAutocomplete-option': {
              color: '#fafafa',
              fontSize: '0.875rem',
              '&:hover': { bgcolor: '#1a1a1a' },
              '&[aria-selected="true"]': { bgcolor: '#27272a' },
            },
          },
        },
      }}
    />
  );

  if (canRead && isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {deleteError != null && deleteError.length > 0 ? (
        <Alert
          severity={'error'}
          onClose={() => setDeleteError(null)}
          sx={{
            mb: 2,
            bgcolor: '#27272a',
            color: '#f87171',
            border: '1px solid #3f3f46',
          }}
        >
          <AlertTitle sx={{ color: '#f87171' }}>{'Error'}</AlertTitle>
          {deleteError}
        </Alert>
      ) : null}

      {formError ? (
        <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2, bgcolor: '#27272a', border: '1px solid #3f3f46' }}>
          {formError}
        </Alert>
      ) : null}

      {!canRead && canCreate && (
        <Typography variant="body2" sx={{ color: '#71717a', mb: 2 }}>
          You do not have access to the venue list. You can still add a venue below; it will appear for others after you gain list access.
        </Typography>
      )}

      {canCreate && (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            sx={{ flexWrap: 'wrap' }}
          >
            <Box sx={{ ...formRowControlColumnSx, flexBasis: { sm: 160 }, minWidth: { xs: '100%', sm: 140 } }}>
              <TextField
                label="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                size="small"
                margin="none"
                disabled={saving}
                helperText=" "
                FormHelperTextProps={{ sx: { minHeight: 40, m: 0, mt: '6px' } }}
                sx={{ ...formRowFieldBaseSx }}
              />
            </Box>
            <Box sx={{ ...formRowControlColumnSx, flexBasis: { sm: 220 }, minWidth: { xs: '100%', sm: 180 } }}>
              <TextField
                label="Domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                size="small"
                margin="none"
                disabled={saving}
                placeholder="https://example.com"
                error={Boolean(newDomain.trim()) && !isValidPublishVenueDomainUrl(newDomain)}
                helperText={
                  newDomain.trim() && !isValidPublishVenueDomainUrl(newDomain)
                    ? 'Enter a valid URL (http or https, with a hostname).'
                    : ' '
                }
                FormHelperTextProps={{ sx: { minHeight: 40, m: 0, mt: '6px' } }}
                sx={{ ...formRowFieldBaseSx }}
              />
            </Box>
            <Box sx={{ ...formRowControlColumnSx, flexBasis: { sm: 200 }, minWidth: { xs: '100%', sm: 160 } }}>
              {platformAutocomplete(newPlatform, setNewPlatform, 'Platform')}
            </Box>
            <Box
              sx={{
                ...formRowControlColumnSx,
                flexBasis: { sm: 200 },
                minWidth: { xs: '100%', sm: 160 },
                flexGrow: { sm: 0 },
                maxWidth: { sm: 320 },
              }}
            >
              <TextField
                label="CMS Authentication Token"
                type={showNewCMSKey ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <PasswordVisibilityToggle
                      visible={showNewCMSKey}
                      onToggle={() => setShowNewCMSKey((v) => !v)}
                      label="CMS authentication token"
                      disabled={saving}
                    />
                  ),
                }}
                value={newCMSKey}
                onChange={(e) => setNewCMSKey(e.target.value)}
                size="small"
                margin="none"
                disabled={saving}
                autoComplete="new-password"
                helperText="Same value as News Portal admin → Integrations (generate there, paste here)."
                FormHelperTextProps={{
                  sx: {
                    minHeight: 40,
                    m: 0,
                    mt: '6px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  },
                }}
                inputProps={{
                  title:
                    'Must match the CMS authentication token from News Portal admin (Integrations). Paste after generating it there.',
                }}
                sx={{ ...formRowFieldBaseSx }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                mt: formRowInlineControlMt,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={newEnabled}
                    onChange={(_, c) => {
                      if (c && !newCMSKey.trim()) {
                        setFormError('Set a CMS authentication token before enabling this venue.');
                        return;
                      }
                      setFormError(null);
                      setNewEnabled(c);
                    }}
                    disabled={saving}
                    sx={{ color: '#71717a' }}
                  />
                }
                label="Enabled"
                sx={{ color: '#d4d4d8', m: 0 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mt: formRowInlineControlMt }}>
              <Tooltip title="Add venue">
                <span>
                  <IconButton
                    color="primary"
                    disabled={addDisabled}
                    onClick={() => void handleAdd()}
                    sx={{ color: '#818cf8' }}
                    size="small"
                    aria-label="Add venue"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Stack>
        </Stack>
      )}

      {canRead && venues.length === 0 && (
        <Typography variant="body2" sx={{ color: '#71717a', mb: 2 }}>
          No publish venues yet.
        </Typography>
      )}

      {venues.length > 0 && (
        <Stack spacing={1.5}>
          {venues.map((v) => (
            <Stack
              key={v.id}
              spacing={2}
              sx={{ p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#18181b' }}
            >
              {editingId === v.id ? (
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    flexWrap="wrap"
                  >
                    <Box sx={{ ...formRowControlColumnSx, flexBasis: { sm: 160 }, minWidth: { xs: '100%', sm: 140 } }}>
                      <TextField
                        label="Name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        size="small"
                        margin="none"
                        disabled={saving}
                        helperText=" "
                        FormHelperTextProps={{ sx: { minHeight: 40, m: 0, mt: '6px' } }}
                        sx={{ ...formRowFieldBaseSx }}
                      />
                    </Box>
                    <Box sx={{ ...formRowControlColumnSx, flexBasis: { sm: 220 }, minWidth: { xs: '100%', sm: 180 } }}>
                      <TextField
                        label="Domain"
                        value={editDomain}
                        onChange={(e) => setEditDomain(e.target.value)}
                        size="small"
                        margin="none"
                        disabled={saving}
                        error={Boolean(editDomain.trim()) && !isValidPublishVenueDomainUrl(editDomain)}
                        helperText={
                          editDomain.trim() && !isValidPublishVenueDomainUrl(editDomain)
                            ? 'Enter a valid URL (http or https, with a hostname).'
                            : ' '
                        }
                        FormHelperTextProps={{ sx: { minHeight: 40, m: 0, mt: '6px' } }}
                        sx={{ ...formRowFieldBaseSx }}
                      />
                    </Box>
                    <Box sx={{ ...formRowControlColumnSx, flexBasis: { sm: 200 }, minWidth: { xs: '100%', sm: 160 } }}>
                      {platformAutocomplete(editPlatform, setEditPlatform, 'Platform')}
                    </Box>
                    <Box
                      sx={{
                        ...formRowControlColumnSx,
                        flexBasis: { sm: 200 },
                        minWidth: { xs: '100%', sm: 160 },
                        flexGrow: { sm: 0 },
                        maxWidth: { sm: 320 },
                      }}
                    >
                      <TextField
                        label="CMS Authentication Token"
                        type={showEditCMSKey ? 'text' : 'password'}
                        InputProps={{
                          endAdornment: (
                            <PasswordVisibilityToggle
                              visible={showEditCMSKey}
                              onToggle={() => setShowEditCMSKey((v) => !v)}
                              label="CMS authentication token"
                              disabled={saving || editClearCMSKey}
                            />
                          ),
                        }}
                        value={editCMSKey}
                        onChange={(e) => setEditCMSKey(e.target.value)}
                        size="small"
                        margin="none"
                        disabled={saving || editClearCMSKey}
                        autoComplete="new-password"
                        helperText={
                          editClearCMSKey
                            ? 'Token will be removed on save.'
                            : 'Leave blank to keep the current token; enter a new value to replace it.'
                        }
                        FormHelperTextProps={{
                          sx: {
                            minHeight: 40,
                            m: 0,
                            mt: '6px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          },
                        }}
                        sx={{ ...formRowFieldBaseSx }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        mt: formRowInlineControlMt,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editEnabled}
                            onChange={(_, c) => {
                              if (c && !editCMSKey.trim() && !v.cms_key_configured) {
                                setFormError('Set a CMS authentication token before enabling this venue.');
                                return;
                              }
                              setFormError(null);
                              setEditEnabled(c);
                            }}
                            disabled={saving}
                            sx={{ color: '#71717a' }}
                          />
                        }
                        label="Enabled"
                        sx={{ color: '#d4d4d8', m: 0 }}
                      />
                    </Box>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editClearCMSKey}
                        onChange={(_, c) => setEditClearCMSKey(c)}
                        disabled={saving}
                        sx={{ color: '#71717a' }}
                      />
                    }
                    label="Remove stored CMS authentication token"
                    sx={{ color: '#d4d4d8', alignItems: 'flex-start', ml: 0 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Save">
                      <span>
                        <IconButton
                          color="primary"
                          disabled={editSaveDisabled}
                          onClick={() => void handleSaveEdit()}
                          sx={{ color: '#818cf8' }}
                          size="small"
                          aria-label="Save venue"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        onClick={cancelEdit}
                        disabled={saving}
                        size="small"
                        sx={{ color: '#a1a1aa' }}
                        aria-label="Cancel edit"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                    <Typography sx={{ fontWeight: 600, color: '#fafafa' }}>{v.name}</Typography>
                    <Typography variant="body2" sx={{ color: '#a1a1aa', wordBreak: 'break-all' }}>
                      {v.domain?.trim() ? v.domain : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#71717a' }}>
                      {v.platform ? platformLabelForValue(v.platform) : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#71717a' }}>
                      CMS authentication token: {v.cms_key_configured ? 'stored' : 'not set'}
                    </Typography>
                  </Stack>
                  {!v.cms_key_configured ? (
                    <Tooltip title="Set a CMS authentication token before enabling this venue.">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(v.enabled)}
                            onChange={(_, c) => void handleToggleEnabled(v, c)}
                            disabled={saving || !canUpdate}
                          />
                        }
                        label="Enabled"
                        sx={{ color: '#d4d4d8' }}
                      />
                    </Tooltip>
                  ) : (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(v.enabled)}
                          onChange={(_, c) => void handleToggleEnabled(v, c)}
                          disabled={saving || !canUpdate}
                        />
                      }
                      label="Enabled"
                      sx={{ color: '#d4d4d8' }}
                    />
                  )}
                  {canUpdate && (
                    <Tooltip title="Edit">
                      <IconButton aria-label="Edit venue" onClick={() => startEdit(v)} disabled={saving} sx={{ color: '#a1a1aa' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        aria-label="Delete venue"
                        onClick={() => void handleDelete(v.id)}
                        disabled={saving}
                        sx={{ color: '#f87171' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};
